import * as logger from "../utils/logger";
import Api from "../services/api";
import { CoachingActions } from "../modules/coaching/store/actions";
import { CoachingNotificationService } from "../modules/coaching/services/coachingNotificationService";
import { CoachingSessionService } from "../modules/coaching/services/coachingSessionService";
import { DriveStore } from "../store/reducers";
import { ICoachingComment } from "../modules/coaching/store/types";
import { NumberOfSyncRetries } from "./base";
import { isDuplicateException, isInvalidPermissionsException } from "../utils/errorHandler/error.types";
import { COACHING_SYNC_LOG_PREFIX } from "../modules/coaching/constants";
import { isApiError, isTerminalError } from "./coachingSyncErrors";

/** Stage tag appended to the shared root so the whole flow greps by `[CoachingSync]`. */
const LOG_PREFIX = `${ COACHING_SYNC_LOG_PREFIX } [Comment]`;

export type TCommentFlushOutcome = "allSettled" | "pending";

/**
 * Posts a session's offline comments and records their sync state. The orchestrator
 * (CoachingSync) owns session resolution and ordering; this class only knows how to push a
 * comment and how each error resolves.
 */
export class CoachingCommentSync {
    constructor(
        private readonly api: Api,
        private readonly store: DriveStore,
        private readonly sessionService: CoachingSessionService,
        private readonly notifications: CoachingNotificationService
    ) {}

    /**
     * Push every comment oldest-first, settling each in the store as it goes:
     *   - synced / duplicate  → marked synced, move on
     *   - no permission       → the driver lost access, so every remaining comment would fail the
     *                           same way: fail them all, notify once, and stop
     *   - terminal            → the server rejected it and will keep doing so: fail just this
     *                           comment and keep going
     *   - anything else       → default: treat as transient and retry on a bounded budget, stopping
     *                           the batch until next cycle; fail only once the budget is exhausted
     *
     * Returns "allSettled" when nothing remains pending (so the completion may proceed) or
     * "pending" when a comment is awaiting another cycle.
     */
    async push(userId: string, sessionId: string, comments: ICoachingComment[]): Promise<TCommentFlushOutcome> {
        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } pushing session ${ sessionId } with ${ comments.length } comments`,
            this.api.getUser()
        );
        for (let i = 0; i < comments.length; i++) {
            const comment = comments[i];
            try {
                await this.sessionService.addComment(sessionId, comment.comment, comment.id);
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } comment ${ comment.id } synced`,
                    this.api.getUser()
                );
                this.markSynced(userId, sessionId, comment.id);
                continue;
            } catch (error) {
                if (!isApiError(error)) {
                    // An unexpected runtime fault, not a server verdict — fail this comment and let
                    // the exception surface rather than silently retrying a bug.
                    logger.log(
                        logger.Source.DEBUG,
                        `${ LOG_PREFIX } comment ${ comment.id } failed with unexpected error, failing and rethrowing`,
                        this.api.getUser()
                    );
                    this.markFailed(userId, sessionId, comment.id, "syncFailed");
                    throw error;
                }
                const apiError = error;

                if (isDuplicateException(apiError)) {
                    logger.log(
                        logger.Source.DEBUG,
                        `${ LOG_PREFIX } comment ${ comment.id } treated as already synced (duplicate)`,
                        this.api.getUser()
                    );
                    this.markSynced(userId, sessionId, comment.id);
                    continue;
                }

                if (isInvalidPermissionsException(apiError)) {
                    logger.error(
                        logger.Source.SYNC,
                        `${ LOG_PREFIX } Coaching comments rejected — no permission on session ${ sessionId }`,
                        this.api.getUser(),
                        apiError
                    );
                    comments.slice(i).forEach((c) => this.markFailed(userId, sessionId, c.id, "syncFailed"));
                    this.notifications.notifyCommentDroppedDueToNoPermission(userId, sessionId);
                    return "allSettled";
                }

                if (isTerminalError(apiError)) {
                    logger.error(
                        logger.Source.SYNC,
                        `${ LOG_PREFIX } Coaching comment ${ comment.id } rejected permanently: ${ apiError.message }`,
                        this.api.getUser(),
                        apiError
                    );
                    this.markFailed(userId, sessionId, comment.id, "syncFailed");
                    continue;
                }

                // Default path: treat anything not explicitly terminal as transient and retry on a
                // bounded budget, stopping the batch until next cycle; fail only once it runs out.
                const remaining = (comment._syncCounter ?? NumberOfSyncRetries) - 1;
                if (remaining <= 0) {
                    logger.log(
                        logger.Source.DEBUG,
                        `${ LOG_PREFIX } comment ${ comment.id } exhausted retry budget, failing`,
                        this.api.getUser()
                    );
                    this.markFailed(userId, sessionId, comment.id, "syncFailed");
                    continue;
                }
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } comment ${ comment.id } hit transient error, pausing batch with ${ remaining } retries remaining`,
                    this.api.getUser()
                );
                this.store.dispatch(
                    CoachingActions.updatePendingCommentState({
                        userId,
                        sessionId,
                        commentId: comment.id,
                        patch: { _syncCounter: remaining }
                    })
                );
                return "pending";
            }
        }
        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } session ${ sessionId } all comments settled`,
            this.api.getUser()
        );
        return "allSettled";
    }

    /** Fail every comment because the session can no longer accept it (finalized/obsolete/unusable). */
    drop(userId: string, sessionId: string, comments: ICoachingComment[], reason: string): void {
        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } dropping session ${ sessionId } with ${ comments.length } comments (reason: ${ reason })`,
            this.api.getUser()
        );
        comments.forEach((c) => this.markFailed(userId, sessionId, c.id, reason));
    }

    private markSynced(userId: string, sessionId: string, commentId: string): void {
        this.store.dispatch(
            CoachingActions.updatePendingCommentState({
                userId,
                sessionId,
                commentId,
                patch: {
                    modified: undefined,
                    syncStatus: "synced",
                    dateTime: new Date().toISOString(),
                    failureReason: undefined,
                    _syncCounter: undefined
                }
            })
        );
    }

    private markFailed(userId: string, sessionId: string, commentId: string, reason: string): void {
        this.store.dispatch(
            CoachingActions.updatePendingCommentState({
                userId,
                sessionId,
                commentId,
                patch: { modified: undefined, syncStatus: "failed", failureReason: reason }
            })
        );
    }
}
